import React, { useCallback } from "react"
import { IconEnvelopeFill18 as EnvelopeIcon } from "nucleo-ui-fill-18"
import { IconUserOutline18 as User } from "nucleo-ui-outline-18"
import { z } from "zod"

import { MembershipRole } from "@productlane/db/browser"
import {
	Dialog,
	Form,
	FormSelect,
	FormTextarea,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import {
	invalidEmailMessage,
	mergeEmails,
	partitionEmails,
	splitEmails,
} from "@/lib/invite-emails"
import { showInviteErrorToast } from "@/lib/invite-error"
import { useSession } from "@/lib/session"
import { ROLE_OPTIONS } from "./member-roles"

const MAX_INVITES_PER_BATCH = 100

const FormSchema = z.object({
	emails: z.string().superRefine((value, ctx) => {
		const { valid, invalid } = partitionEmails(splitEmails(value))
		if (invalid.length > 0) {
			ctx.addIssue({ code: "custom", message: invalidEmailMessage(invalid) })
		} else if (valid.length === 0) {
			ctx.addIssue({ code: "custom", message: "Enter an email address." })
		} else if (mergeEmails([], valid).length > MAX_INVITES_PER_BATCH) {
			ctx.addIssue({
				code: "custom",
				message: `You can invite up to ${MAX_INVITES_PER_BATCH} people at once.`,
			})
		}
	}),
	role: z.nativeEnum(MembershipRole),
})
type FormValues = z.infer<typeof FormSchema>

const DEFAULT_VALUES: FormValues = { emails: "", role: MembershipRole.USER }

const ROLE_SELECT_OPTIONS = ROLE_OPTIONS.map((option) => ({
	label: (
		<>
			{option.label} -{" "}
			<span className="text-secondary">{option.description}</span>
		</>
	),
	value: option.role,
}))

function invitesSentToast(emails: string[]) {
	return {
		title: `${emails.length} ${emails.length === 1 ? "invite" : "invites"} sent`,
		description:
			emails.length === 1
				? `We emailed ${emails[0]} a link to join your workspace.`
				: "We emailed each person a link to join your workspace.",
	}
}

interface IProps {
	open: boolean
	onClose: () => void
}

export const InviteMemberModal = React.memo(({ open, onClose }: IProps) => {
	const context = api.useUtils()
	const { session } = useSession()
	const domain = session?.email.split("@")[1] ?? "company.com"

	const inviteMutation = api.workspace.inviteMembers.useMutation()

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: DEFAULT_VALUES,
	})

	const submit = useCallback(
		async ({ emails, role }: FormValues) => {
			const invites = mergeEmails([], splitEmails(emails))
			let results
			try {
				results = await inviteMutation.mutateAsync({
					invites: invites.map((email) => ({ email })),
					role,
				})
			} catch (error) {
				showInviteErrorToast(error as { message?: string })
				return
			}
			await Promise.all([
				context.workspace.members.invalidate(),
				context.workspace.invitedMembers.invalidate(),
			])

			const sent = results.filter((r) => r.ok).map((r) => r.email)
			const failed = results.filter((r) => !r.ok)
			if (sent.length > 0) {
				toast.success({ ...invitesSentToast(sent), icon: EnvelopeIcon })
			}
			if (failed.length > 0) {
				toast.info({
					title:
						failed.length === 1
							? `Couldn't invite ${failed[0]?.email}`
							: `Couldn't invite ${failed.length} people`,
					description:
						failed.length === 1
							? (failed[0]?.error ?? "Try again in a moment.")
							: failed
									.map((f) => `${f.email}: ${f.error ?? "Failed to invite"}`)
									.join("\n"),
					icon: EnvelopeIcon,
				})
				form.reset({ emails: failed.map((f) => f.email).join(", "), role })
				return
			}
			form.reset(DEFAULT_VALUES)
			onClose()
		},
		[context, form, inviteMutation, onClose],
	)

	return (
		<Dialog
			onOpenChange={(value) => {
				if (!value) {
					onClose()
				}
			}}
			isOpen={open}
			title={
				<div className="flex items-center">
					<User className="mr-2 h-4 w-4 flex-none" />
					Invite to your workspace
				</div>
			}
		>
			<Form form={form} className="relative" onSubmit={submit}>
				<div className="relative mt-3 flex flex-col space-y-4">
					<div className="space-y-3">
						<FormTextarea
							name="emails"
							label="Email"
							placeholder={`email@${domain}, email2@${domain}...`}
							minRows={3}
							autoComplete="off"
							autoFocus
							onKeyDown={(event) => {
								if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
									event.preventDefault()
									event.currentTarget.form?.requestSubmit()
								}
							}}
						/>
						<FormSelect
							name="role"
							label="Role"
							options={ROLE_SELECT_OPTIONS}
						/>
					</div>
					<SubmitButton
						className="ml-auto"
						isLoading={inviteMutation.isLoading}
					>
						Send invites
					</SubmitButton>
				</div>
			</Form>
		</Dialog>
	)
})
